import { useCallback, useRef } from "react";

// Default equality: compare by `value` property, fall back to reference equality
// Mirrors the dropdown's isOptionEqualToValue behavior
function defaultIsEqual(a, b) {
	if (a == null || b == null) return a === b;
	if (a.value != null && b.value != null) return a.value === b.value;
	return a === b;
}

// Find index of target in options array using equality check
function indexOfOption(options, target, isEqual) {
	if (!Array.isArray(options) || target == null) return -1;
	return options.findIndex((o) => isEqual(o, target));
}

// Compute range selection: merge current selection with all options between anchor and target
// Uses displayed option order (respects filters)
// Normalizes anchor/target order (extends both up and down)
// Falls back to adding target if no valid anchor
export function computeRangeSelection({ options, anchorOption, targetOption, currentValue, isEqual = defaultIsEqual, isRangeable }) {
	const selection = Array.isArray(currentValue) ? [...currentValue] : [];
	if (targetOption == null) return selection;

	// A sentinel option (e.g. "All") can be excluded from range spans so a
	// Shift-range never accidentally toggles it. When the target itself is not
	// rangeable, ignore the interaction entirely.
	const rangeable = typeof isRangeable === "function" ? isRangeable : () => true;
	if (!rangeable(targetOption)) return selection;

	const targetIdx = indexOfOption(options, targetOption, isEqual);
	if (targetIdx === -1) return selection;

	const anchorIdx = indexOfOption(options, anchorOption, isEqual);

	// No usable anchor -> behave like a normal single add of the target.
	if (anchorIdx === -1) {
		const already = selection.some((s) => isEqual(s, targetOption));
		return already ? selection : [...selection, targetOption];
	}

	const start = Math.min(anchorIdx, targetIdx);
	const end = Math.max(anchorIdx, targetIdx);
	const rangeOptions = options.slice(start, end + 1).filter(rangeable);

	// Merge the range into the current selection, skipping anything already present.
	const merged = [...selection];
	for (const opt of rangeOptions) {
		if (!merged.some((s) => isEqual(s, opt))) {
			merged.push(opt);
		}
	}
	return merged;
}

// Hook for Shift+Arrow (keyboard) and Shift+click (mouse) range selection in multi-select dropdowns
// Returns: getAutocompleteProps() to spread on <Autocomplete>, handleOptionClick() for renderOption onClick
export default function useShiftRangeSelect({ getOptions, getValue, onRangeSelect, isEqual = defaultIsEqual, isRangeable }) {
// Last highlighted option via keyboard/mouse
const highlightedRef = useRef(null);
// Shift key state (true if held, false otherwise)
const shiftHeldRef = useRef(false);

// Anchor = last selected option; ranges extend from anchor to target
const getAnchor = useCallback(() => {
		const value = getValue?.() || [];
		return value.length > 0 ? value[value.length - 1] : null;
	}, [getValue]);

	const applyRange = useCallback(
		(targetOption) => {
			if (targetOption == null) return;
			const next = computeRangeSelection({
				options: getOptions?.() || [],
				anchorOption: getAnchor(),
				targetOption,
				currentValue: getValue?.() || [],
				isEqual,
				isRangeable,
			});
			onRangeSelect?.(next);
		},
		[getOptions, getAnchor, getValue, onRangeSelect, isEqual, isRangeable]
	);

	// Extend range when Shift+Arrow navigates; "reason" indicates event type (keyboard/mouse/touch)
	const onHighlightChange = useCallback(
		(event, option, reason) => {
			highlightedRef.current = option || null;
			if (option == null) return;
			// Extend the range when Shift is held during keyboard navigation.
			// `reason` is "keyboard" for arrow keys; treat a missing reason as
			// keyboard too, since mouse/touch highlight changes set it explicitly.
			if (shiftHeldRef.current && reason !== "mouse" && reason !== "touch") {
				applyRange(option);
			}
		},
		[applyRange]
	);

	// Track whether Shift is currently held. event.shiftKey covers the case where
	// Shift was already down before this key (e.g. Shift+ArrowDown).
	const onKeyDown = useCallback((event) => {
		shiftHeldRef.current = event.shiftKey || event.key === "Shift";
	}, []);

	const onKeyUp = useCallback((event) => {
		if (event.key === "Shift") shiftHeldRef.current = false;
	}, []);

	// If Shift held: extend range to clicked option, prevent default toggle
	const handleOptionClick = useCallback(
		(event, option) => {
			if (!event?.shiftKey) return false;
			event.preventDefault();
			event.stopPropagation();
			applyRange(option);
			highlightedRef.current = option;
			return true;
		},
		[applyRange]
	);

	const getAutocompleteProps = useCallback(
		() => ({
			onHighlightChange,
			onKeyDown,
			onKeyUp,
		}),
		[onHighlightChange, onKeyDown, onKeyUp]
	);

	return { getAutocompleteProps, handleOptionClick, onHighlightChange, onKeyDown, onKeyUp };
}
